import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../config/axios';
import { Customer } from '../../types/invoice';

interface CustomerState {
  customers: Customer[];
  loading: boolean;
  error: string | null;
}

const initialState: CustomerState = {
  customers: [],
  loading: false,
  error: null,
};

export const fetchCustomers = createAsyncThunk(
  'customers/fetchCustomers',
  async (search: string | undefined, { rejectWithValue }) => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await api.get(`/customers${params}`);
      return response.data.customers as Customer[];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Nepodařilo se načíst zákazníky');
    }
  }
);

/** Split "account/bankcode" into { bankAccount, bankCode } for the API */
function splitBankAccount(combined: string): { bankAccount: string; bankCode: string } {
  if (!combined) return { bankAccount: '', bankCode: '' };
  const idx = combined.lastIndexOf('/');
  if (idx === -1) return { bankAccount: combined.trim(), bankCode: '' };
  return {
    bankAccount: combined.slice(0, idx).trim(),
    bankCode: combined.slice(idx + 1).trim(),
  };
}

/** Convert form data (combined bank account) to API payload */
function toApiPayload(data: any) {
  const { bankAccount: combined, ...rest } = data;
  const { bankAccount, bankCode } = splitBankAccount(combined || '');
  return { ...rest, bankAccount, bankCode };
}

export const createCustomer = createAsyncThunk(
  'customers/createCustomer',
  async (data: Omit<Customer, 'id' | 'userId' | 'createdAt' | 'updatedAt'>, { rejectWithValue }) => {
    try {
      const response = await api.post('/customers', toApiPayload(data));
      return response.data.customer as Customer;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || { error: 'Nepodařilo se vytvořit zákazníka' });
    }
  }
);

export const updateCustomer = createAsyncThunk(
  'customers/updateCustomer',
  async (
    { id, data }: { id: number; data: Partial<Omit<Customer, 'id' | 'userId' | 'createdAt' | 'updatedAt'>> },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.put(`/customers/${id}`, toApiPayload(data));
      return response.data.customer as Customer;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || { error: 'Nepodařilo se upravit zákazníka' });
    }
  }
);

export const deleteCustomer = createAsyncThunk(
  'customers/deleteCustomer',
  async (id: number, { rejectWithValue }) => {
    try {
      await api.delete(`/customers/${id}`);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Nepodařilo se smazat zákazníka');
    }
  }
);

export const createCustomerFromInvoice = createAsyncThunk(
  'customers/createCustomerFromInvoice',
  async (
    { invoiceId, data }: { invoiceId: number; data: Partial<Omit<Customer, 'id' | 'userId' | 'createdAt' | 'updatedAt'>> },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(`/customers/from-invoice/${invoiceId}`, toApiPayload(data));
      return response.data.customer as Customer;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || { error: 'Nepodařilo se vytvořit zákazníka' });
    }
  }
);

const customerSlice = createSlice({
  name: 'customers',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action: PayloadAction<Customer[]>) => {
        state.loading = false;
        state.customers = action.payload;
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(createCustomer.fulfilled, (state, action: PayloadAction<Customer>) => {
        state.customers.push(action.payload);
        state.customers.sort((a, b) => a.name.localeCompare(b.name, 'cs'));
      })

      .addCase(updateCustomer.fulfilled, (state, action: PayloadAction<Customer>) => {
        const idx = state.customers.findIndex((c) => c.id === action.payload.id);
        if (idx !== -1) state.customers[idx] = action.payload;
        state.customers.sort((a, b) => a.name.localeCompare(b.name, 'cs'));
      })

      .addCase(deleteCustomer.fulfilled, (state, action: PayloadAction<number>) => {
        state.customers = state.customers.filter((c) => c.id !== action.payload);
      })

      .addCase(createCustomerFromInvoice.fulfilled, (state, action: PayloadAction<Customer>) => {
        state.customers.push(action.payload);
        state.customers.sort((a, b) => a.name.localeCompare(b.name, 'cs'));
      });
  },
});

export const { clearError } = customerSlice.actions;
export default customerSlice.reducer;
